import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, Plus, Trash2, AlertTriangle, CheckCircle2, Repeat } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ReminderDialog } from "@/components/ReminderDialog";

function time12(t) {
  if (!t) return "9:00 AM";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ap}`;
}

export function RemindersCard({ entityType, entityId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const key = ["reminders", entityType, entityId];

  const { data: items = [] } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get("/follow-ups", { params: { entity_type: entityType, entity_id: entityId } })).data,
    refetchInterval: 60000,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["follow-ups"] });
  };
  const markDone = async (id) => {
    try { await api.post(`/follow-ups/${id}/done`); refresh(); toast.success("Reminder completed"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/follow-ups/${id}`); refresh(); toast.success("Reminder removed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const pending = items.filter((i) => i.status !== "done");
  const done = items.filter((i) => i.status === "done");

  return (
    <Card className="p-5" data-testid="reminders-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-sm text-slate-800 flex items-center gap-1.5">
          <CalendarClock className="h-4 w-4 text-brand" /> Tasks & Reminders
        </h3>
        <Button size="sm" className="h-8 gap-1.5 bg-brand hover:bg-brand/90 text-white" onClick={() => setOpen(true)} data-testid="schedule-reminder-btn">
          <Plus className="h-3.5 w-3.5" /> Schedule
        </Button>
      </div>

      {pending.length === 0 && done.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">No reminders yet. Schedule one to get a timely nudge.</p>
      ) : (
        <div className="space-y-2">
          {pending.map((f) => (
            <div key={f.id} className={`flex items-start justify-between gap-3 p-2.5 rounded-md border ${f.overdue ? "border-red-200 bg-red-50/60" : "border-slate-200 bg-slate-50"}`} data-testid={`reminder-${f.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 break-words">{f.note}</p>
                {f.next_action && <p className="text-xs text-slate-500 mt-0.5">Next: {f.next_action}</p>}
                <p className={`text-xs font-medium mt-1 flex items-center gap-1 flex-wrap ${f.overdue ? "text-red-600" : "text-slate-500"}`}>
                  {f.overdue && <AlertTriangle className="h-3 w-3" />}
                  {formatDate(f.due_date)} · {time12(f.due_time)}{f.overdue ? " · overdue" : ""}
                  {f.recurrence && f.recurrence !== "none" && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-brand bg-brand/10 px-1.5 py-0.5 rounded-full capitalize"><Repeat className="h-2.5 w-2.5" />{f.recurrence}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => markDone(f.id)} title="Mark done" data-testid={`reminder-done-${f.id}`}><Check className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => remove(f.id)} title="Delete" data-testid={`reminder-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
          {done.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-slate-100" data-testid={`reminder-${f.id}`}>
              <div className="min-w-0 flex-1 flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                <p className="text-sm text-slate-400 line-through truncate">{f.note}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-300 hover:text-red-600" onClick={() => remove(f.id)} data-testid={`reminder-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}

      <ReminderDialog open={open} onOpenChange={setOpen} entityType={entityType} entityId={entityId} onSaved={refresh} />
    </Card>
  );
}
